'use client';

import type { SortInfoItem } from '@weberus/shared-types';
import type { FC } from 'react';
import { useState } from 'react';
import { BottomSheet, TextButton, Dialog } from '@yanolja-rnd/nol-design-system';
import { useAtomValue, useSetAtom } from 'jotai';
import { parse } from 'qs';
import { isNil, omitBy } from 'lodash-es';
import { IcInfo, IcDone } from '@yanolja-rnd/nol-icons';
import { useHashModalAppRouter } from '@weberus/next-libs/feature/hash-modal-app-router';
import { generateHashedId } from '@weberus/utils';
import { MODAL__SWIPE_BACK__OPTION } from '@weberus/ui/utils/swipe-back-options';
import { deviceAtom } from '@/app/_shared/store/device';
import { cn } from '@/app/_shared/utils/tw-cn';
import {
  searchFilterSortAtom,
  searchTypeKeyAtom,
  sessionKeyAtom,
  sortInfoAtom,
} from '@/app/_shared/store/search-filter';
import { updateQueryAndScroll } from '@/app/_components/feature/global-filter-modal/utils/update-query-and-scroll';
import { updateFilterStorage } from '../../feature/plp-srp-filter/filter-storage';

interface SortModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onSortChange: (sortObj: SortInfoItem) => void;
}

export const SortModal: FC<SortModalProps> = ({ isOpen, closeModal, onSortChange }) => {
  const { deviceType } = useAtomValue(deviceAtom);
  const sortInfo = useAtomValue(sortInfoAtom);
  const setSort = useSetAtom(searchFilterSortAtom);
  const sessionKey = useAtomValue(sessionKeyAtom);
  const searchTypeKey = useAtomValue(searchTypeKeyAtom);

  const [isInfoDialog, setIsInfoDialog] = useHashModalAppRouter({
    id: `hash-${generateHashedId('info-dialog')}`,
    swipeBackOption: MODAL__SWIPE_BACK__OPTION,
  });

  const [isInfoIndex, setIsInfoIndex] = useState(0);

  const handleClickSortInfo = async (item: SortInfoItem) => {
    const currentQuery = parse(new URLSearchParams(window.location.search).toString());

    const newQuery = omitBy({ ...currentQuery, sort: item.key }, (value) => isNil(value) || value === '');

    // 클라이언트 로그 전송
    onSortChange(item);

    // 세션 스토리지 업데이트
    updateFilterStorage({
      query: currentQuery,
      scrollY: 0,
      sessionKey,
      searchType: currentQuery[searchTypeKey] as string | undefined,
    });

    // 정렬 상태 업데이트
    setSort(item.key);
    // 모달 닫기
    closeModal();

    // URL 업데이트
    updateQueryAndScroll(newQuery);
  };

  const handleInfoClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setIsInfoDialog(true);
    setIsInfoIndex(index);
  };

  const handlePositiveClick = () => {
    setIsInfoDialog(false);
  };

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        os={deviceType}
        header="정렬"
        height="contents"
        zIndex={20}
        dialogOptions={{
          className: 'w-[360px]',
        }}
        onClose={closeModal}>
        <div className={cn(deviceType !== 'etc' && 'mx-16 mb-8', 'flex flex-col gap-4')}>
          {sortInfo?.items?.map((item: SortInfoItem, index: number) => (
            <div key={index} className="flex w-full items-center justify-between">
              <TextButton
                key={index}
                mobileSize="medium"
                pcSize="medium"
                className={cn(
                  `h-48 w-full justify-between gap-8 rounded-8 px-16 text-left ${item.selected ? 'bg-fill-neutral-weak text-text-neutral-main !typography-subtitle-16-bold' : '!typography-subtitle-16-regular'}`,
                )}
                onClick={() => {
                  handleClickSortInfo(item);
                }}>
                <div className="flex items-center gap-8">
                  {item.title}
                  {!!item.description && (
                    <IcInfo
                      className="text-primary text-text-neutral-main"
                      height={16}
                      width={16}
                      onClick={(e: React.MouseEvent) => {
                        handleInfoClick(e, index);
                      }}
                    />
                  )}
                </div>
                {!!item.selected && <IcDone className="text-primary ml-10 h-24 w-24 bg-transparent" />}
              </TextButton>
            </div>
          ))}
        </div>
      </BottomSheet>
      <Dialog
        id="sort-modal-info"
        show={isInfoDialog}
        zIndex={30}
        title={sortInfo?.items?.[isInfoIndex].title}
        description={sortInfo?.items?.[isInfoIndex].description}
        positiveButton={{
          text: '확인',
          onClick: handlePositiveClick,
        }}
      />
    </>
  );
};
